import { DataDrivenPropertyValueSpecification } from 'maplibre-gl';
import { MetarDisplayMode } from '../../../settings/settings';
import { StyleFactory, WeatherLayerSpecification } from '../styling';
import { getBarbColorExpression, getBarbsExpression } from './common';

export const metarLayers: StyleFactory = (baseId, settings) => {
    const layers: WeatherLayerSpecification[] = [];

    // Barbs
    if (
        settings.airport.displayMode === MetarDisplayMode.categorySkyWind ||
        settings.airport.displayMode === MetarDisplayMode.categorySkyWindValues
    ) {
        layers.push({
            minzoom: 5,
            id: baseId + '-barb',
            source: baseId,
            type: 'symbol',
            layout: {
                // prettier-ignore
                'icon-image': getBarbsExpression(['get', 'wind'], ['get', 'dir']),
                'icon-allow-overlap': true,
                'icon-size': ['global-state', 'wind-barbs-size'],
                'icon-anchor': ['case', ['==', ['get', 'dir'], 0xfffe], 'center', 'bottom'],
                'icon-rotate': ['get', 'dir'],
                'icon-rotation-alignment': 'map'
            },
            paint: {
                'icon-color': getBarbColorExpression(settings, ['get', 'wind'])
            }
        });
    }

    // Sky
    if (
        settings.airport.displayMode >= MetarDisplayMode.categorySky &&
        settings.airport.displayMode <= MetarDisplayMode.categorySkyWindValues
    ) {
        layers.push({
            type: 'symbol',
            id: baseId + '-icon',
            pickable: true,
            source: baseId,
            layout: {
                // prettier-ignore
                'icon-image': [
                    'case', 
                    ['==', ['get', 'sky'], 1], 'fe-weather:sky-clear',
                    ['==', ['get', 'sky'], 2], 'fe-weather:sky-sky-clear',
                    ['==', ['get', 'sky'], 3], 'fe-weather:sky-few',
                    ['==', ['get', 'sky'], 4], 'fe-weather:sky-scattered',
                    ['==', ['get', 'sky'], 5], 'fe-weather:sky-broken',
                    'fe-weather:sky-unknown'
                ],
                'icon-allow-overlap': true,
                'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.2, 5, 0.5, 10, 0.8]
            },
            paint: {
                // prettier-ignore
                'icon-color': [
                    'case',
                    ['==', ['get', 'c'], 1], '#00dc00',
                    ['==', ['get', 'c'], 2], '#ffff00',
                    ['==', ['get', 'c'], 3], '#fa7d00',
                    ['==', ['get', 'c'], 4], '#ff0000',
                    ['==', ['get', 'c'], 5], '#ff00ff',
                    'rgb(150,150,150)'
                ]
            }
        });
    } else {
        let getColor: DataDrivenPropertyValueSpecification<string>;
        if (settings.airport.displayMode === MetarDisplayMode.ceiling) {
            // prettier-ignore
            getColor = [
                'case',
                ['<', ['get', 'ceilV'], 0], 'rgb(150,150,150)', // null
                ['<', ['get', 'ceilV'], 200], 'rgb(255,0,255)',
                ['<', ['get', 'ceilV'], 500], 'rgb(255,128,165)',
                ['<', ['get', 'ceilV'], 1_000], 'rgb(255,165,0)',
                ['<', ['get', 'ceilV'], 2_000], 'rgb(255,255,103)',
                ['<', ['get', 'ceilV'], 3_000], 'rgb(103,255,103)',
                ['<', ['get', 'ceilV'], 5_000], 'rgb(0,131,255)',
                ['<', ['get', 'ceilV'], 10_000], 'rgb(51,220,255)',
                ['>=', ['get', 'ceilV'], 10_000], 'rgb(135,206,255)',
                'rgb(150,150,150)'
            ];
        } else if (settings.airport.displayMode === MetarDisplayMode.visibility) {
            // prettier-ignore
            getColor = [
                'case',
                ['<', ['get', 'v'], 0],'rgb(150,150,150)', // null
                ['<', ['get', 'v'], 0.25],'rgb(255,0,255)',
                ['<', ['get', 'v'], 0.5],'rgb(255,0,0)',
                ['<', ['get', 'v'], 1.0],'rgb(255,128,165)',
                ['<', ['get', 'v'], 2.0],'rgb(255,165,0)',
                ['<', ['get', 'v'], 3.0],'rgb(255,255,103)',
                ['<', ['get', 'v'], 5.0],'rgb(103,255,103)',
                ['<', ['get', 'v'], 10.0],'rgb(0,131,255)',
                ['>=', ['get', 'v'], 10.0],'rgb(135,206,255)',
                'rgb(150,150,150)'
            ];
        } else {
            // prettier-ignore
            getColor = [
                'case',
                ['==', ['get', 'c'], 1], '#00dc00',
                ['==', ['get', 'c'], 2], '#ffff00',
                ['==', ['get', 'c'], 3], '#fa7d00',
                ['==', ['get', 'c'], 4], '#ff0000',
                ['==', ['get', 'c'], 5], '#ff00ff',
                'rgb(150,150,150)'
            ];
        }
        layers.push({
            pickable: true,
            type: 'circle',
            id: baseId + '-circle',
            source: baseId,
            paint: {
                // prettier-ignore
                'circle-radius': [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0,
                    1,
                    5,
                    5,
                    10,
                    7
                ],
                'circle-color': getColor
            }
        });
    }

    // Text
    if (settings.airport.displayMode === MetarDisplayMode.categorySkyWindValues) {
        layers.push(
            {
                id: baseId + '-alt',
                minzoom: 5,
                source: baseId,
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'alt'],
                    'text-offset': [1, -1],
                    'text-allow-overlap': true,
                    'text-anchor': 'left',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': settings.airport.showMetarDetailInColor ? 'rgb(128,128,255)' : ['global-state', 'text-color']
                }
            },
            {
                id: baseId + '-ceil',
                minzoom: 5,
                source: baseId,
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'ceil'],
                    'text-offset': [1, 0],
                    'text-allow-overlap': true,
                    'text-anchor': 'left',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            {
                type: 'symbol',
                id: baseId + '-id',
                source: baseId,
                minzoom: 5,
                layout: {
                    'text-field': ['get', 'id'],
                    'text-offset': [1, 1],
                    'text-allow-overlap': true,
                    'text-anchor': 'left',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            {
                type: 'symbol',
                id: baseId + '-dp',
                source: baseId,
                minzoom: 5,
                layout: {
                    'text-field': ['get', 'dp'],
                    'text-offset': [-1, 1],
                    'text-allow-overlap': true,
                    'text-anchor': 'right',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': settings.airport.showMetarDetailInColor ? 'rgb(0,210,0)' : ['global-state', 'text-color']
                }
            },
            {
                type: 'symbol',
                id: baseId + '-temp',
                source: baseId,
                minzoom: 5,
                layout: {
                    'text-field': ['get', 'temp'],
                    'text-offset': [-1, -1],
                    'text-allow-overlap': true,
                    'text-anchor': 'right',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': settings.airport.showMetarDetailInColor ? 'rgb(255,0,0)' : ['global-state', 'text-color']
                }
            },
            {
                type: 'symbol',
                id: baseId + '-vis',
                source: baseId,
                minzoom: 5,
                layout: {
                    'text-field': ['get', 'vis'],
                    'text-offset': [-1, 0],
                    'text-allow-overlap': true,
                    'text-anchor': 'right',
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10
                },
                paint: {
                    'text-color': settings.airport.showMetarDetailInColor ? 'rgb(255,0,255)' : ['global-state', 'text-color']
                }
            }
        );
    } else {
        layers.push({
            type: 'symbol',
            id: baseId + '-id',
            source: baseId,
            minzoom: 5,
            layout: {
                'text-field': ['get', 'id'],
                'text-offset': [0.5, 0.5],
                'text-allow-overlap': true,
                'text-anchor': 'top-left',
                'text-font': ['Open Sans Regular'],
                'text-size': 10
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        });
    }
    return layers;
};
